import { useEffect, useRef, useState } from "react";
import type { Beat } from "../scenario/types";
import { audio } from "../lib/audio";
import { TerminalHelperModal } from "./TerminalHelperModal";
import { activeTtsLang } from "../lib/locale";
import { SkipButton } from "./SkipButton";
import "./newspaper.css";
import { tx, txr } from "../lib/tx";

const DIMENSION_LABEL: Record<string, string> = {
  identity: "Identity",
  information: "Information",
  money: "Money",
  operations: "Operations",
};

type DebriefConfig = NonNullable<Beat["terminalDebrief"]>;
type DebriefTopic = DebriefConfig["topics"][number];

/**
 * Full-course recap rendered on the SAME terminal kiosk chrome as the
 * Episode-12 ending (`.terminal`/`.term-bezel`/`.term-screen--kiosk` — see
 * Terminal.tsx), reached by a short camera push-in right after the vishing
 * call. Unlike Terminal.tsx's flat `lines: string[]`, this walks a list of
 * TOPICS (one per attack-exposure dimension the learner has been watching
 * all course via ExposureMap.tsx) — each topic auto-narrates its own 1-2
 * recap lines, then optionally offers a "Learn more" helper button that
 * opens a small modal with a looping animated graphic (TerminalHelperModal).
 * Deliberately a standalone component (NOT a Terminal.tsx variant) so the
 * shared kiosk file used by every other course never has to change shape for
 * this course-specific recap layout.
 */
export function TerminalDebrief({ config, onComplete }: { config: DebriefConfig; onComplete: () => void }) {
  const [topicIdx, setTopicIdx] = useState(0);
  const [lineShown, setLineShown] = useState(0);
  const [introDone, setIntroDone] = useState(false);
  const [helperTopic, setHelperTopic] = useState<DebriefTopic | null>(null);
  const [showCases, setShowCases] = useState(false);
  const started = useRef(false);
  const alive = useRef(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    alive.current = true;
    const voiceOpts = { voice: "onyx", lang: activeTtsLang() };
    void audio.prefetchSpeech(config.intro, voiceOpts);
    config.topics.forEach((t) => t.lines.forEach((l) => void audio.prefetchSpeech(l, voiceOpts)));
    return () => {
      alive.current = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const voiceOpts = { voice: "onyx", lang: activeTtsLang() };
    const run = async () => {
      audio.sceneTransition();
      await audio.speak(config.intro, voiceOpts);
      if (!alive.current) return;
      setIntroDone(true);
      for (let ti = 0; ti < config.topics.length; ti++) {
        if (!alive.current) return;
        setTopicIdx(ti);
        setLineShown(0);
        const topic = config.topics[ti];
        for (let li = 0; li < topic.lines.length; li++) {
          if (!alive.current) return;
          setLineShown(li + 1);
          await audio.speak(topic.lines[li], voiceOpts);
        }
      }
      if (alive.current) setTopicIdx(config.topics.length);
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll the recap body to the newest revealed line/topic.
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [topicIdx, lineShown]);

  const skip = () => {
    alive.current = false;
    audio.cancelSpeech();
    setIntroDone(true);
    setTopicIdx(config.topics.length);
    onComplete();
  };

  const allDone = topicIdx >= config.topics.length;
  const visibleCount = Math.min(topicIdx + 1, config.topics.length);
  const shownLinesFor = (i: number) => (i < topicIdx ? config.topics[i].lines.length : i === topicIdx ? lineShown : 0);

  return (
    <div className="terminal termd-host">
      <div className="term-bezel">
        <span className="term-bezel-cam" aria-hidden />
        <div className="term-screen term-screen--kiosk termd-screen">
          <div className="term-head">
            <div className="term-head-status">
              <span className="term-head-tag term-head-tag--warn">{tx("DEBRIEF")}</span>
              <span className="term-head-kicker">{tx("POST-INCIDENT REVIEW")}</span>
            </div>
            <div className="term-head-top">
              <span className="term-head-badge" aria-hidden>{tx("N")}</span>
              <span className="term-head-title">{tx("Today's decisions, reviewed")}</span>
            </div>
            <div className="term-head-rule" aria-hidden />
          </div>

          <div className="termd-body" ref={bodyRef}>
            {introDone && (
              <p className="termd-intro">{config.intro}</p>
            )}
            {config.topics.slice(0, visibleCount).map((topic, i) => {
              const shown = shownLinesFor(i);
              const topicDone = shown >= topic.lines.length;
              return (
                <div className="termd-topic" key={topic.id}>
                  <div className="termd-topic-head">
                    <span className={`termd-dim-dot termd-dim-dot--${topic.dimension}`} aria-hidden />
                    <span className="termd-dim-label">{DIMENSION_LABEL[topic.dimension]}</span>
                    <h3 className="termd-topic-title">{topic.title}</h3>
                  </div>
                  <div className="termd-topic-lines">
                    {topic.lines.slice(0, shown).map((l, li) => (
                      <p key={li} className="termd-topic-line">{l}</p>
                    ))}
                  </div>
                  {topicDone && topic.helper && (
                    <button
                      type="button"
                      className="termd-helper-btn"
                      onClick={() => setHelperTopic(topic)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M12 8.4h.01M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                      {topic.helper.label}
                    </button>
                  )}
                </div>
              );
            })}
            {!allDone && (
              <span className="term-caret-blink termd-caret" aria-hidden>{"\u2588"}</span>
            )}
          </div>

          <div className="term-cta-row">
            <div className={`termd-cta-main${config.realCases && config.realCases.cases.length > 0 ? " has-cases" : ""}`}>
              <button
                type="button"
                className={`btn term-continue ${allDone ? "" : "term-continue--wait"}`}
                disabled={!allDone}
                onClick={onComplete}
              >
                <span className="term-cta-chevron" aria-hidden>{"\u203A"}</span>
                {allDone ? config.continueLabel ?? tx("Continue to the review quiz") : tx("See why this matters →")}
              </button>
              {config.realCases && config.realCases.cases.length > 0 && (
                <button type="button" className="btn termd-cases-btn" onClick={() => setShowCases(true)}>
                  {config.realCases.buttonLabel ?? tx("See real-life fraud cases")}
                </button>
              )}
            </div>
            <SkipButton onClick={skip} />
          </div>
        </div>
      </div>
      {helperTopic?.helper && (
        <TerminalHelperModal
          kicker={helperTopic.helper.kicker}
          title={helperTopic.helper.modalTitle}
          lede={helperTopic.helper.lede}
          blocks={helperTopic.helper.blocks}
          facts={helperTopic.helper.facts}
          visual={helperTopic.helper.visual}
          onClose={() => setHelperTopic(null)}
        />
      )}
      {showCases && config.realCases && config.realCases.cases.length > 0 && (
        <NewspaperCases config={config.realCases} onClose={() => setShowCases(false)} />
      )}
    </div>
  );
}

type RealCases = NonNullable<DebriefConfig["realCases"]>;
function NewspaperCases({ config, onClose }: { config: RealCases; onClose: () => void }) {
  const [idx, setIdx] = useState(0);
  const cases = config.cases;
  const c = cases[idx];
  const prev = () => setIdx((i) => Math.max(0, i - 1));
  const next = () => setIdx((i) => Math.min(cases.length - 1, i + 1));
  return (
    <div className="news-overlay" role="dialog" aria-label={tx("Real-life cases")}>
      <div className="news-paper">
        <div className="news-masthead">
          <span className="news-mast-title">{config.masthead ?? tx("THE LEDGER")}</span>
          <button type="button" className="news-close" aria-label={tx("Close")} onClick={onClose}>{"\u2715"}</button>
        </div>
        <div className="news-page">
          <div className="news-date">{c.date}</div>
          <h2 className="news-headline">{c.headline}</h2>
          {c.standfirst && <p className="news-standfirst">{c.standfirst}</p>}
          <div className="news-cols">
            {c.image && <div className="news-figure"><img src={c.image} alt="" /><span className="news-figcap">{txr("Illustration · not a real photograph", {})}</span></div>}
            <div className="news-sections">
              {c.sections.map((s) => (
                <div className="news-sec" key={s.h}>
                  <h3>{s.h}</h3>
                  <p>{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="news-foot">
          <button type="button" className="btn news-nav" disabled={idx === 0} onClick={prev}>{"\u2039"} {tx("Previous")}</button>
          <span className="news-count">{txr("Case {v} of {length}", { v: idx + 1, length: cases.length })}</span>
          <div className="news-foot-right">
            {idx + 1 < cases.length ? (
              <button type="button" className="btn news-nav news-nav--primary" onClick={next}>{tx("Next")} {"\u203A"}</button>
            ) : (
              <button type="button" className="btn news-nav news-nav--primary" onClick={onClose}>{tx("Back to the debrief")}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
